/**
 * Single shared scroll driver for the cinematic landing animations.
 *
 * The landing page scrolls inside a custom container (`.page-scroll-root`,
 * `height: 100dvh; overflow-y: auto`) rather than the window, so every
 * scroll-driven primitive subscribes here instead of attaching its own listener.
 * One passive `scroll` + `resize` listener feeds a `requestAnimationFrame`-throttled
 * broadcast to all subscribers, which keeps dozens of animated elements cheap and
 * frame-aligned.
 *
 * Because `.page-scroll-root` fills the viewport (top: 0, height: 100dvh), an
 * element's `getBoundingClientRect()` (window-relative) lines up with the
 * container's visible box — so primitives can reason in plain viewport terms.
 */

export interface ScrollState {
    /** The scrollable container element (null before it mounts). */
    container: HTMLElement | null;
    /** Current scrollTop of the container. */
    scrollTop: number;
    /** Visible height of the container (≈ viewport height for the page). */
    viewportHeight: number;
    /** Visible width of the container. */
    viewportWidth: number;
}

type ScrollCallback = (state: ScrollState) => void;

const subscribers = new Set<ScrollCallback>();
let container: HTMLElement | null = null;
let attached = false;
let scheduled = false;
let rafId = 0;

/** Resolve (and cache) the page scroll container, re-querying if it detached. */
export function getScrollContainer(): HTMLElement | null {
    if (container && container.isConnected) return container;
    container = typeof document !== 'undefined' ? document.querySelector<HTMLElement>('.page-scroll-root') : null;
    return container;
}

function readState(): ScrollState {
    const el = getScrollContainer();
    const fallbackH = typeof window !== 'undefined' ? window.innerHeight : 0;
    const fallbackW = typeof window !== 'undefined' ? window.innerWidth : 0;
    return {
        container: el,
        scrollTop: el ? el.scrollTop : 0,
        viewportHeight: el ? el.clientHeight : fallbackH,
        viewportWidth: el ? el.clientWidth : fallbackW,
    };
}

function emit(): void {
    scheduled = false;
    const state = readState();
    subscribers.forEach((cb) => cb(state));
}

function schedule(): void {
    if (scheduled) return;
    scheduled = true;
    rafId = requestAnimationFrame(emit);
}

function attach(): void {
    if (attached) return;
    const el = getScrollContainer();
    if (!el) return;
    el.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    attached = true;
}

function detach(): void {
    if (!attached) return;
    getScrollContainer()?.removeEventListener('scroll', schedule);
    window.removeEventListener('resize', schedule);
    cancelAnimationFrame(rafId);
    attached = false;
}

/**
 * Subscribe to scroll updates. The callback fires once on the next frame with the
 * current state (so animations settle to the right position on mount), then on
 * every subsequent scroll/resize. Returns an unsubscribe function.
 */
export function subscribeScroll(cb: ScrollCallback): () => void {
    subscribers.add(cb);
    attach();
    schedule();
    return () => {
        subscribers.delete(cb);
        if (subscribers.size === 0) detach();
    };
}

/** True when the user has requested reduced motion. */
export function prefersReducedMotion(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
